import {Injectable} from '@angular/core';
import {BehaviorSubject, map, Observable, Subject} from 'rxjs';
import {HeaderFilter, SourceNameSearchRadius} from '../model/asax-header.model';
import {filter} from "rxjs/operators";

const defaultCalibratorHeaderFilter: HeaderFilter = {
  id: 'calibratorObservations',
  elasticParserType: 'calibration-data',
  value: false,
  valueParsed: false
};

@Injectable({
  providedIn: 'root'
})
export class AsaxHeaderService {
  private readonly headerFiltersById = new Map<string, HeaderFilter>([['calibratorObservations', defaultCalibratorHeaderFilter]]);
  private readonly headerFiltersSubject = new BehaviorSubject<Map<string, HeaderFilter>>(this.headerFiltersById);

  private readonly allHeaderFiltersRemovedSubject = new Subject<undefined>();
  private readonly headerFilterRemovedSubject = new Subject<string>();

  private readonly sourceNameSearchRadiusSubject = new Subject<SourceNameSearchRadius>();

  private readonly raDecSearchRadius = new Subject<SourceNameSearchRadius>();

  private readonly projectAbstractSearchInputTextSubject = new BehaviorSubject<string>(undefined);
  private readonly publicationAbstractSearchInputTextSubject = new BehaviorSubject<string>(undefined);

  getHeaderFilters(): Observable<HeaderFilter[]> {
    return this.headerFiltersSubject.asObservable().pipe(
      map(filtersMap => [...filtersMap.values()])
    );
  }

  upsertHeaderFilter(headerFilter: HeaderFilter): void {
    this.headerFiltersById.set(headerFilter?.id, headerFilter);
    this.headerFiltersSubject.next(new Map(this.headerFiltersById));
  }

  getHeaderFilterIsDisabled(id: string): Observable<boolean> {
    return this.headerFiltersSubject.asObservable().pipe(
      map((filters) => filters.get(id)),
      map((filter) => filter?.isDisabled)
    );
  }

  removeHeaderFilters() {
    this.headerFiltersById.clear();

    this.headerFiltersById.set('calibratorObservations', defaultCalibratorHeaderFilter);
    this.headerFiltersSubject.next(new Map(this.headerFiltersById));
    this.allHeaderFiltersRemovedSubject.next(undefined);
  }

  headerFiltersRemoved(): Observable<undefined> {
    return this.allHeaderFiltersRemovedSubject.asObservable();
  }

  removeHeaderFilter(headerFilterId: string): void {
    this.headerFiltersById.delete(headerFilterId);

    this.headerFiltersSubject.next(new Map(this.headerFiltersById));
    this.headerFilterRemovedSubject.next(headerFilterId);
  }

  headerFilterRemoved(headerFilterId: string): Observable<string> {
    return this.headerFilterRemovedSubject.asObservable().pipe(
      filter(id => id === headerFilterId)
    );
  }

  getSourceNameSearchRadius(): Observable<SourceNameSearchRadius> {
    return this.sourceNameSearchRadiusSubject.asObservable();
  }

  setSourceNameSearchRadius(sourceNameSearchRadius: SourceNameSearchRadius): void {
    this.sourceNameSearchRadiusSubject.next(sourceNameSearchRadius);
  }

  getRaDecSearchRadius(): Observable<SourceNameSearchRadius> {
    return this.raDecSearchRadius.asObservable();
  }

  setRaDecSearchRadius(sourceNameSearchRadius: SourceNameSearchRadius): void {
    this.raDecSearchRadius.next(sourceNameSearchRadius);
  }

  getProjectAbstractSearchInputText(): Observable<string> {
    return this.projectAbstractSearchInputTextSubject.asObservable();
  }

  setProjectAbstractSearchInputText(text: string): void {
    this.projectAbstractSearchInputTextSubject.next(text);
  }

  getPublicationAbstractSearchInputText(): Observable<string> {
    return this.publicationAbstractSearchInputTextSubject.asObservable();
  }

  setPublicationAbstractSearchInputText(text: string): void {
    this.publicationAbstractSearchInputTextSubject.next(text);
  }
}
